import { UserTourStatus } from '@/enums/user-tour-status.enum';
import untypedTourConfigs from './tourConfigs.json';
import { TourKeys } from './tour-keys';
import type { TourDetails } from '@/types/auth.types';

/**
 * Get started advances to output education; later coachmarks stay owed in order. Progress is the
 * persisted `currentStep` — the first step not yet seen — and the tour is COMPLETED once its final
 * configured coachmark has been dismissed.
 *
 * `currentUser.tours` is not refetched after a tour write, so this page load's writes are held here
 * and layered over the persisted record.
 */
const progressThisPageLoad = new Map<string, number>();

const stepIds: string[] = untypedTourConfigs[TourKeys.ToolBuilder].steps.map((step) => step.stepId);

export const INTRO_STEP_INDEX = 0;
export const LAST_STEP_INDEX = stepIds.length - 1;

export function getToolBuilderStepIndex(stepId: string) {
  return stepIds.indexOf(stepId);
}

function getProgress(userId: string | undefined, tour: TourDetails | undefined): number {
  if (tour?.status === UserTourStatus.COMPLETED) return stepIds.length;
  const persisted = tour?.currentStep ?? INTRO_STEP_INDEX;
  const thisPageLoad = (userId && progressThisPageLoad.get(userId)) || INTRO_STEP_INDEX;
  return Math.max(persisted, thisPageLoad);
}

/** Step to resume in Tool mode, or null when the tour is unknown/completed. */
export function getToolBuilderResumeStep(userId: string | undefined, tour: TourDetails | undefined): number | null {
  if (!tour?.status || tour.status === UserTourStatus.COMPLETED) return null;
  const progress = getProgress(userId, tour);
  if (progress > LAST_STEP_INDEX) return null;
  return progress;
}

/** True while `stepIndex` is still owed to this user — i.e. they have neither seen nor skipped it. */
export function canShowToolBuilderStep(
  userId: string | undefined,
  tour: TourDetails | undefined,
  stepIndex: number,
): boolean {
  return getProgress(userId, tour) <= stepIndex;
}

/** Records that `stepIndex` has been seen, and returns the progress to persist. Only moves forward. */
export function recordToolBuilderStepSeen(userId: string | undefined, stepIndex: number): number {
  const next = stepIndex + 1;
  if (!userId) return next;
  const progress = Math.max(progressThisPageLoad.get(userId) ?? INTRO_STEP_INDEX, next);
  progressThisPageLoad.set(userId, progress);
  return progress;
}

export function isLastToolBuilderStep(stepIndex: number) {
  return stepIndex >= LAST_STEP_INDEX;
}

export function resetToolBuilderProgress(userId?: string) {
  if (userId) {
    progressThisPageLoad.delete(userId);
    return;
  }
  progressThisPageLoad.clear();
}
